import React, { useEffect, useCallback } from "react";
import styled, { keyframes, css } from "styled-components";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { useOrdersStore } from "../../stores/orders/orders-store";

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(40px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1300;
  padding: 16px;
`;

const ModalContent = styled.div`
  width: 90%;
  max-width: 400px;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
  text-align: center;
  color: #222;
  ${css`
    animation: ${fadeInUp} 0.35s cubic-bezier(0.23, 1, 0.32, 1);
  `}
  position: relative;
  padding: 32px 24px 24px 24px;
  outline: none;
`;

const IconWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 16px;
`;

const ErrorHeader = styled.div`
  font-size: 1.5em;
  font-weight: 700;
  color: #d32f2f;
  margin-bottom: 8px;
  letter-spacing: 0.5px;
`;

const ErrorMessage = styled.div`
  font-size: 1.1em;
  color: #333;
  margin-bottom: 24px;
  text-align: left;
  word-break: break-word;
  white-space: pre-line;
`;

const OkButton = styled.button`
  width: 100%;
  padding: 12px 0;
  background: linear-gradient(90deg, #fd3c51 0%, #ff7e5f 100%);
  color: #fff;
  font-weight: 700;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  cursor: pointer;
  transition: background 0.2s, opacity 0.2s;
  box-shadow: 0 2px 8px rgba(253, 60, 81, 0.08);
  margin-top: 8px;
  &:hover {
    opacity: 0.92;
  }
`;

interface ErrorModalProps {
  isOpen: boolean;
  message: string;
}

const ErrorModal: React.FC<ErrorModalProps> = ({ isOpen, message }) => {
  const { setErrorModal } = useOrdersStore();

  const handleClose = useCallback(() => {
    setErrorModal(false);
  }, [setErrorModal]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        handleClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, handleClose]);

  if (!isOpen) return null;

  return (
    <ModalOverlay role="presentation">
      <ModalContent
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-desc"
        tabIndex={-1}
      >
        <IconWrapper>
          <ErrorOutlineIcon style={{ fontSize: 48, color: "#fd3c51" }} />
        </IconWrapper>
        <ErrorHeader id="alert-dialog-title">Alert</ErrorHeader>
        <ErrorMessage id="alert-dialog-desc">
          {message || (
            <>
              <b>YOU ARE NOT RECEIVING ORDERS</b>
              <br />
              <br />
              This is due to your tablet not being connected to the internet.
              Restart your tablet, modem or router. If problem persists, contact{" "}
              <a
                href="tel:1300407891"
                style={{ color: "#fd3c51", textDecoration: "underline" }}
              >
                1300 407 891
              </a>
              .
            </>
          )}
        </ErrorMessage>
        <OkButton onClick={handleClose} autoFocus>
          Okay
        </OkButton>
      </ModalContent>
    </ModalOverlay>
  );
};

export default ErrorModal;
